<script setup lang="ts">
import { reactive } from 'vue';
import {
  CdrInput, CdrButton, CdrLink, CdrText, CdrGrid, CdrImg,
} from '@rei/cedar';
import BannerComponent from '../../common/components/BannerComponent.vue';
import { useTags } from '../../common/composables/useTags';
import { useAnalytics } from '../../common/composables/useAnalytics';
import useAdobeExperiment from '../../common/composables/useAdobeExperiment';

const state = reactive({
  memberNumber: '',
  inputValidationError: null,
  showLinkMembershipError: false,
  showLinkMembershipSuccess: false,
});

const { adobeExperimentValue: adobeExperimentLinkMembershipCategory } = useAdobeExperiment({
  adobeTargetLocation: 'help-link-membership-category',
  tagControlIdentifier: 'HELP_CENTER_LINK_MEMBERSHIP_CONTROL',
  tagVariantIdentifier: 'HELP_CENTER_LINK_MEMBERSHIP_VARIANT',
});

const validateField = () => {
  state.showLinkMembershipError = false;

  if (!state.memberNumber) {
    state.inputValidationError = "Member number shouldn't be empty";
    return false;
  }
  if (state.memberNumber && !/^[0-9]+$/.test(state.memberNumber)) {
    state.inputValidationError = 'Member numbers only include numbers. Please try again.';
    return false;
  }

  state.inputValidationError = null;
  return true;
};

const handleSubmit = async () => {
  if (!validateField()) return;

  useAnalytics('sendClickAnalytics', {
    linkName: useTags('getTag', 'MEMBERSHIP_REWARDS_LINK_MEMBERSHIP_CLICK'),
  });

  try {
    const changeMemberNumberResponse = await fetch('/rest/user/memberNumber', {
      method: 'POST',
      cache: 'no-cache',
      body: `memberNumber=${state.memberNumber}`,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/x-www-form-urlencoded',
        'Cache-Control': 'no-store',
      },
    });

    if (changeMemberNumberResponse.ok) {
      state.showLinkMembershipSuccess = true;
    } else {
      state.showLinkMembershipError = true;
    }
  } catch (e) {
    state.showLinkMembershipError = true;
  }
};
</script>

<template>
  <div
    v-if="adobeExperimentLinkMembershipCategory === 'B'"
    class="link-membership-wrapper"
  >
    <BannerComponent
      v-if="state.showLinkMembershipSuccess"
      :type="'success'"
    >
      Your membership is now linked to your online account.
    </BannerComponent>

    <CdrGrid
      v-else
      gutter="medium"
      class="link-membership"
    >
      <div class="link-membership__form-section">
        <h2 scale="2">
          Link your membership to your online account
        </h2>
        <CdrText
          class="link-membership__message"
        >
          Enter your member number to access your full member benefits and use rewards online.
        </CdrText>
        <form
          class="link-membership__form"
          @submit.prevent="handleSubmit()"
        >
          <div class="link-membership__form-input-wrapper">
            <CdrInput
              v-model="state.memberNumber"
              label="Member number"
              name="memberNumber"
              background="secondary"
              required
              :error="state.inputValidationError"
              @blur="validateField()"
              @input="validateField()"
            >
              <template
                #error
              >
                <span class="link-membership__form-error">{{ state.inputValidationError }}</span>
              </template>
            </CdrInput>
            <CdrLink
              class="link-membership__lookup"
              href="/membership/lookup"
              modifier="standalone"
            >
              Forgot your member number?
            </CdrLink>
          </div>
          <CdrButton
            :class="[
              'link-membership__form--submit',
            ]"
            type="submit"
          >
            Link membership
          </CdrButton>
        </form>

        <BannerComponent
          v-if="state.showLinkMembershipError"
          :type="'error'"
          :class="'link-membership__alert'"
        >
          Your member number doesn't match our records. Please re-enter it or
          <CdrLink href="/help?article=contact-rei">
            contact us
          </CdrLink> for help.
        </BannerComponent>
      </div>
      <div class="link-membership__join-section">
        <CdrImg
          src="https://www.rei.com/dam/three-multicolored-acorns"
          class="link-membership__join-section-image link-membership__join-section-image--mobile"
          alt="REI Co-op Membership Banner"
        />
        <CdrImg
          src="https://www.rei.com/dam/acorn-cluster-color-small.png"
          class="link-membership__join-section-image link-membership__join-section-image--desktop"
          alt="REI Co-op Membership Banner"
        />
        <div class="copy-content">
          <CdrText class="link-membership__join-section-heading">
            Not a member?
          </CdrText>
          <CdrText class="link-membership__join-section-body">
            Join once for $30 and and enjoy a lifetime of benefits—<span
              class="link-membership__text--bold"
            >
              10% back
            </span>
            on eligible purchases, free standard shipping and more!
          </CdrText>
        </div>
        <CdrButton
          tag="a"
          href="/membership"
          modifier="secondary"
          class="link-membership__join-section-cta"
        >
          Join today
        </CdrButton>
      </div>
    </CdrGrid>
  </div>
</template>

<style lang="scss" scoped>
@import '@rei/cdr-tokens/dist/rei-dot-com/scss/cdr-tokens.scss';

.link-membership-wrapper {
  max-width: 117rem;
  margin: auto;
  margin-bottom: 2.5rem;

  @include cdr-lg-mq-down {
    padding: 24px 16px;
  }
}

.link-membership {
  background-color: $cdr-color-background-primary;
  border: 1px solid rgba(66, 59, 47, 0.75);
  border-radius: $cdr-space-half-x;
  box-shadow: 0 0.4rem 0.4rem rgba(12, 11, 8, 0.2);
  padding: $cdr-space-two-x;
  grid-template-columns: repeat(12, 1fr);

  &__text--bold {
    @include cdr-text-utility-sans-strong-300;

    @include cdr-sm-mq-down {
      font-size: 1.4rem;
    }
  }

  @include cdr-sm-mq-down {
    margin: $cdr-space-three-quarter-x auto $cdr-space-one-x auto;
    grid-template-columns: repeat(8, 1fr);
    row-gap: $cdr-space-two-x;
  }

  &__form-section {
    grid-column: 1 / span 8;
    display: flex;
    flex-direction: column;

    @include cdr-sm-mq-down {
      grid-column: 1 / -1;
    }
  }

  &--icon {
    padding: $cdr-space-half-x;
    position: absolute;
    right: $cdr-space-one-x;
    top: $cdr-space-one-x;

    @include cdr-lg-mq-down {
      right: $cdr-space-half-x;
      top: $cdr-space-half-x;
    }
  }

  h2 {
    @include cdr-text-utility-serif-700;
    margin-bottom: $cdr-space-half-x;

    @include cdr-sm-mq-down {
      @include cdr-text-utility-serif-600;
      margin-top: 0;
    }
  }

  &__message {
    margin-bottom: $cdr-space-three-quarter-x;
    @include cdr-text-utility-sans-300;
  }

  &__form {
    display: flex;
    place-items: center;
    gap: $cdr-space-half-x;
    flex-direction: row;
    align-items: start;

    @include cdr-sm-mq-down {
      flex-direction: column;
    }
  }

  &__form-input-wrapper {
    width: 250px;

    @include cdr-sm-mq-down {
      width: 100%;
    }
  }

  &__form-error {
    @include cdr-text-utility-sans-200;
  }

  &__form--submit {
    margin-top: 32px;
    place-content: center;

    @include cdr-sm-mq-down {
      margin-top: $cdr-space-half-x;
      width: 100%;
    }
  }

  &__alert {
    margin-top: $cdr-space-half-x;
  }

  &__lookup {
    @include cdr-sm-mq-down {
      font-size: $cdr-text-utility-serif-200-size;
    }
  }

  &__join-section {
    grid-column: 9 / -1;

    &-image--desktop {
      display: inline;

      @include cdr-sm-mq-down {
        display: none;
      }
    }

    &-image--mobile {
      display: inline;

      @include cdr-sm-mq-up {
        display: none;
      }
    }

    &-image {
      width: 62px;
      image-rendering: inherit;
    }

    &-heading {
      @include cdr-text-utility-sans-strong-400;
      margin-bottom: $cdr-space-quarter-x;

      @include cdr-sm-mq-down {
        font-size: $cdr-text-utility-serif-300-size;
        margin-bottom: 0;
      }
    }

    &-body {
      margin-bottom: $cdr-space-one-x;

      @include cdr-sm-mq-down {
        @include cdr-text-utility-sans-200;
        margin-bottom: 0;
      }
    }

    @include cdr-sm-mq-down {
      grid-column: 1 / -1;
      display: grid;
      grid-template-columns: 1fr auto;
      gap: $cdr-space-one-and-a-half-x;

      &-image {
        grid-column: 2 / span 1;
      }

      .copy-content {
        grid-column: 1 / span 1;
        grid-row: 1;
      }

      &-cta {
        grid-row: 2;
        grid-column: 1 / -1;
        place-content: center;
      }
    }
  }
}
</style>
